import { Input } from '@/components/ui/input';
import { useEffect, useRef, useState } from 'react';

import PrimaryButton from '@/components/buttons/PrimaryButton';
import { useUploadDoc } from '@/features/ocr/hooks/useUploadDoc';
import { ProgressOverlay } from '@/features/shared/components/Loaders';
import CloseButton from '@/pages/new-platform/components/CloseButton';
import { File, Upload } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateEvent } from '../../events/hooks/useCreateEvent';
import { EventType } from '../../events/types/EventType';
import { useUploadClassicDoc } from '../hooks/useUploadClassicDoc';

// TODO [TM]: add a mod to handle ocr or classic documents
export function Dropzone() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadType, setUploadType] = useState<'classic' | 'ocr' | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [showSuccessUI, setShowSuccessUI] = useState(false);

  const {
    mutate: mutateOCR,
    isPending,
    isError,
    isSuccess,
    progress,
    error,
  } = useUploadDoc();

  const {
    mutate: mutateClassic,
    isPending: isPendingClassic,
    isError: isErrorClassic,
    isSuccess: isSuccessClassic,
    error: errorClassic,
  } = useUploadClassicDoc();

  const navigate = useNavigate();
  const { sendEvent } = useCreateEvent()
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const isLoading = isPending || isPendingClassic;

  // Preselect upload type if passed via URL
  useEffect(() => {
    if (type === 'ocr' || type === 'classic') {
      setUploadType(type);
    }
  }, [type]);

  useEffect(() => {
    void sendEvent({
      event: EventType.PAGE_VIEW,
      sub_category: "dropzone",
    })
  }, [])

  useEffect(() => {
    if (isSuccess || isSuccessClassic) {
      setShowSuccessUI(true);
    }
  }, [isSuccess, isSuccessClassic]);

  const handleUpload = (file: File) => {
    if (uploadType === 'classic') {
      mutateClassic(file);
    } else if (uploadType === 'ocr') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;

        // First, upload as OCR
        mutateOCR(dataUrl, {
          onSuccess: () => {
            // Then also upload as classic (raw file upload)
            mutateClassic(file);
          },
          onError: (error) => {
            const status = (error as any)?.status;
            const message = (error as any)?.message;
            console.warn(`OCR upload failed [status=${status}]: ${message}`, error);
            mutateClassic(file, {
              onSuccess: () => {
                setShowSuccessUI(true);
              }
            });
          }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (selected) {
      setFile(selected);
      handleUpload(selected);
    }
  };

  // Auto-open file input after selecting upload type
  useEffect(() => {
    if (uploadType && !file) {
      fileInputRef.current?.click();
    }
  }, [uploadType]);

  if (isPending) {
    return <ProgressOverlay progress={progress} showProgress={true} />;
  }

  if (isPendingClassic) {
    return <ProgressOverlay progress={100} showProgress={false} />;
  }

  // TODO [TM]: handle it
  // if (isError) {
  //   const status = (error as any)?.response?.status;
  //   console.log(error)

  //   let message = 'Errore durante il caricamento del documento. Riprova più tardi.';

  //   if ((error as any)?.message?.includes('503')) {
  //     message = 'Il servizio non è disponibile al momento (503). Riprova tra qualche istante.';
  //   } else if ((error as any)?.message?.includes('401')) {
  //     message = 'Sessione scaduta. Effettua di nuovo il login.';
  //   }

  //   return (
  //     <div className="flex flex-col justify-center items-center h-full px-5">
  //       <div className="text-red-600 font-medium text-center mb-4">{message}</div>
  //       <PrimaryButton
  //         text="Torna indietro"
  //         onClick={() => navigate(-1)}
  //         className="!w-full !h-[50px]" pointer={false} disabled={false} />
  //     </div>
  //   );
  // }

  if (showSuccessUI || isError) {
    return (
      <div className="flex flex-col justify-center items-center h-full w-full px-5">
        <div className="text-xl font-semibold text-green-600 mb-2">
          ✅ Documento caricato con successo!
        </div>
        <p className="text-sm text-gray-600 text-center mb-6">
          {uploadType === 'ocr' ? 
            'Il tuo referto è stato caricato e i parametri sono stati estratti.' :
            'Il tuo documento è stato caricato e sarà disponibile nella sezione Documenti caricati da te.'}
        </p>
        <PrimaryButton
          text="Vai ai miei referti"
          className="!w-full !h-[50px] cursor-pointer"
          onClick={() => navigate(-1)} 
          pointer={false} 
          disabled={false} 
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col justify-between h-full w-full px-5'>
      <div>
        <div className="w-full my-[10px] flex justify-end flex-shrink-0">
          <CloseButton
            onClick={() => navigate(-1)}
          />
        </div>
        <div className="font-bold text-[25px] leading-[33px] mt-[10px] mb-[20px]">
          Area caricamento documenti
        </div>
        {/* Upload Type Selector */}
        <label className="mb-2 font-medium mr-4">Tipo di documento</label>
        <select
          value={uploadType}
          onChange={(e) => setUploadType(e.target.value as 'classic' | 'ocr' | '')}
          className="border border-gray-300 rounded px-3 py-2 mb-6"
        >
          <option value="" disabled>Seleziona tipo di caricamento</option>
          <option value="classic">Documento generico</option>
          <option value="ocr">Referto di Laboratorio</option>
        </select>
      </div>
      <div className='relative flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg border-gray-300 hover:border-gray-400 transition-colors cursor-pointer'>
        <Input
          className='absolute inset-0 3/4 h-full opacity-0 cursor-pointer m-0 p-0'
          type='file'
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <div className='flex flex-col items-center justify-center gap-2 pointer-events-none'>
          {file ? (
            <File className='w-12 h-12 text-gray-400' />
          ) : (
            <Upload className='w-12 h-12 text-gray-400' />
          )}

          <p className='text-sm text-gray-500'>
            {file ? file.name : 'Clicca per caricare o trascina il file'}
          </p>
        </div>
      </div>
      {
        file ? <PrimaryButton
          text={isPending ? 'Caricamento in corso' : 'Carica nuovo referto'}
          pointer={!(!file || isPending)}
          disabled={!file || isPending}
          className='!w-full !h-[50px]'
          onClick={() => handleUpload(file!)}
        /> : <div className='!w-full !h-[50px]'></div>
      }

    </div>
  );
}
